import React, {
    useCallback,
    useEffect,
    useRef,
    useState,
} from "react";

import {
    useLocation,
    useNavigate,
} from "react-router-dom";

import {
    ArrowLeft,
    GraduationCap,
    Sparkles,
    UserRound,
} from "lucide-react";

import {
    api,
    APP_NAME,
} from "@/lib/api";


const Login = () => {
    const navigate = useNavigate();
    const location = useLocation();

    const returnTo =
        location.state?.returnTo || null;
    const googleButtonRef = useRef(null);
    const [checking, setChecking] =
        useState(true);

    const [signingIn, setSigningIn] =
        useState(false);


    useEffect(() => {
    let cancelled = false;

    const timeout = window.setTimeout(() => {
        if (!cancelled) {
            setChecking(false);
        }
    }, 2500);

    api.get("/auth/me")
        .then(() => {
            if (cancelled) return;

            window.clearTimeout(timeout);

            navigate(
                returnTo || "/profile",
                {
                    replace: true,
                }
            );
                    })
        .catch(() => {
            if (cancelled) return;

            window.clearTimeout(timeout);
            setChecking(false);
        });

    return () => {
        cancelled = true;
        window.clearTimeout(timeout);
    };
}, [navigate, returnTo]);

const handleGoogleCredential = useCallback(async (
        response
    ) => {
        if (!response?.credential) {
            window.alert(
                "Google sign-in did not return a credential."
            );
            return;
        }

        setSigningIn(true);

        try {
    const loginResponse = await api.post(
        "/auth/google",
        {
            credential:
                response.credential,
        }
    );

    const accessStatus =
        loginResponse?.data?.user
            ?.access_status;

    if (returnTo) {
        navigate(
            returnTo,
            {
                replace: true,
            }
        );

        return;
    }

    navigate(
        "/profile",
        {
            replace: true,
        }
    );
        } catch (error) {
            console.error(
                "Google parent sign-in failed:",
                error
            );

            window.alert(
                error?.response?.data
                    ?.detail ||
                    "Could not sign in with Google."
            );
        } finally {
            setSigningIn(false);
        }
        }, [navigate, returnTo]);
    useEffect(() => {
        const clientId =
            process.env
                .REACT_APP_GOOGLE_CLIENT_ID;

        if (
            !clientId ||
            checking
        ) {
            return;
        }

        const initializeGoogle = () => {
            if (
                !window.google?.accounts?.id ||
                !googleButtonRef.current
            ) {
                return;
            }

            window.google.accounts.id.initialize({
                client_id: clientId,
                callback:
                    handleGoogleCredential,
            });

            googleButtonRef.current.innerHTML =
                "";

            window.google.accounts.id.renderButton(
                googleButtonRef.current,
                {
                    theme: "outline",
                    size: "large",
                    shape: "pill",
                    width: 340,
                    text: "continue_with",
                }
            );
        };

        const existingScript =
            document.querySelector(
                'script[src="https://accounts.google.com/gsi/client"]'
            );

        if (existingScript) {
            initializeGoogle();
            return;
        }

        const script =
            document.createElement(
                "script"
            );

        script.src =
            "https://accounts.google.com/gsi/client";

        script.async = true;
        script.defer = true;

        script.onload =
            initializeGoogle;

        document.head.appendChild(
            script
        );

        return () => {
            script.onload = null;
        };
        }, [
            checking,
            handleGoogleCredential,
        ]);
    


    if (checking) {
        return (
            <div
                className="
                    min-h-screen
                    flex
                    items-center
                    justify-center
                    bg-[#05070d]
                    text-white
                    bg-[radial-gradient(circle_at_top,rgba(124,58,237,0.12),transparent_35%)]
                "
            >
                <p className="text-[#b0b8d6]">
                    Opening Liberi Curiosi...
                </p>
            </div>
        );
    }


    return (
        <div
            className="
                relative
                min-h-screen
                overflow-hidden
                bg-[#05070d]
                text-white
                bg-[radial-gradient(circle_at_top,rgba(124,58,237,0.14),transparent_36%)]
            "
        >
            <div
                className="
                    relative
                    z-10
                    mx-auto
                    flex
                    min-h-screen
                    max-w-6xl
                    items-center
                    justify-center
                    px-6
                    py-12
                "
            >
                <div className="w-full max-w-md">
                    <button
                        type="button"
                        onClick={() =>
                            navigate("/")
                        }
                        className="
                            mb-5
                            inline-flex
                            items-center
                            gap-2
                            rounded-2xl
                            border
                            border-[#ffd166]/35
                            bg-black/70
                            px-4
                            py-2
                            text-sm
                            font-semibold
                            text-[#fff3c4]
                            transition
                            hover:border-[#ffd166]
                            hover:text-[#ffd166]
                        "
                    >
                        <ArrowLeft size={16} />
                        Back to Home
                    </button>

                    <section
                        className="
                            overflow-hidden
                            rounded-[32px]
                            border
                            border-[#ffd166]/45
                            bg-black
                            shadow-[0_0_45px_rgba(255,209,102,0.12)]
                        "
                    >
                        <header
                            className="
                                border-b
                                border-[#ffd166]/20
                                px-7
                                py-7
                                text-center
                            "
                        >
                            <div
                                className="
                                    mx-auto
                                    flex
                                    h-16
                                    w-16
                                    items-center
                                    justify-center
                                    rounded-full
                                    border
                                    border-[#ffd166]/55
                                    bg-[#2b1548]/70
                                    text-[#ffd166]
                                    shadow-[0_0_24px_rgba(168,85,247,0.35)]
                                "
                            >
                                <Sparkles size={28} />
                            </div>

                            <p
                                className="
                                    mt-5
                                    text-xs
                                    font-semibold
                                    uppercase
                                    tracking-[0.28em]
                                    text-[#d8b4fe]
                                "
                            >
                                {APP_NAME || "Liberi Curiosi"}
                            </p>

                            <h1
                                className="
                                    mt-2
                                    font-display
                                    text-4xl
                                    text-[#ffd166]
                                "
                            >
                                Parent Sign In
                            </h1>

                            <p
                                className="
                                    mt-3
                                    text-sm
                                    leading-relaxed
                                    text-[#b0b8d6]
                                "
                            >
                                Enter the parent portal to
                                manage students, classrooms,
                                schedules, and school settings.
                            </p>
                        </header>

                        <div
                            className="
                                space-y-4
                                px-7
                                py-7
                            "
                        >
                            <div className="flex justify-center">
                                <div
                                    ref={googleButtonRef}
                                    className={
                                        signingIn
                                            ? "pointer-events-none opacity-60"
                                            : ""
                                    }
                                />
                            </div>

                            <button
                                type="button"
                                onClick={() =>
                                    navigate(
                                        "/child/login"
                                    )
                                }
                                className="
                                    flex
                                    w-full
                                    items-center
                                    justify-center
                                    gap-3
                                    rounded-2xl
                                    border
                                    border-[#c084fc]/40
                                    bg-[#160d26]/55
                                    px-5
                                    py-4
                                    font-semibold
                                    text-[#e9d5ff]
                                    transition
                                    hover:border-[#c084fc]
                                    hover:bg-[#2b1548]/60
                                "
                            >
                                <GraduationCap
                                    size={19}
                                />
                                Child Sign In
                            </button>
                        </div>
                    </section>
                </div>
            </div>
        </div>
    );
};


export default Login;